import React, { useMemo, useEffect, useCallback, useContext } from "react";
import { useLocation } from "react-router-dom";
import { useIntl } from "react-intl";
import { Icon } from "components/Icon";

import { useWindowWidth } from "hooks";
import {
    ACCOUNT_GET_LIST,
    ACCESS_EXCLUSION_LIST_API,
    ACCOUNT_MANAGERS_REFERENCES_API,
    ACTION_LOGS_GET_LIST_API,
    AM_ACTIVITIES_LIST_API,
    BLOCKED_DESTINATIONS_GET_LIST_API,
    SMS_BLOCKED_DESTINATIONS_GET_LIST_API,
    PAYMENT_REQUEST_LIST_API,
    RATECARD_LIST_ACCOUNT_DIALER,
    MANAGER_ACCOUNT_USER_GET_LIST,
    NEWS_LIST_API,
    INCORPORATED_GROUP_LIST_API,
    SMS_SUPPLIER_GAN_GROUP_GET_LIST_METHOD,
    RATECARD_LIST_OTP_DEFAULT_DIALER,
    RATECARD_LIST_DEFAULT_DIALER,
    RATECARD_LIST_INCORPORATED_DEFAULT_DIALER,
    RATECARD_LIST_INCORPORATED_ACCOUNT_DIALER,
    RATECARD_LIST_OTP_ACCOUNT_DIALER,
    REVOKE_MASS_REVOKE_API,
    SMS_REVOKE_MASS_REVOKE_API,
    DIALER_GROUPS_LIST_API,
    SMS_MANUAL_ACCESS_LIST_GET_LIST_API,
    MANUAL_ACCESS_LIST_GET_LIST_API,
    SUBACC_GET_SUB_ACCOUNT_LIST_METHOD,
    OTP_RULE_API_GET_LIST,
    SMS_SEARCH_GET_LIST,
    ALLOCATION_TEMPLATE_API,
    SMS_ALLOCATION_TEMPLATE_API,
    PROTECTED_RANGES_GET_LIST_API,
    SMS_PROTECTED_RANGES_GET_LIST_API
} from "const/apiMethods";
import { APP_TYPE_ADMIN, transformed_service_data } from "const";
import { servicePick } from "utils";
import m from "definedMessages";
import { buildSearch, ServiceLink } from "components/Navigation/ServiceLink";

import { RtlContext, AppTypeContext } from "../../../App";
import MenuDesktop from "./MenuDesktop";
import MenuMobile from "./MenuMobile";


export default ({
    lang,
    service,
    openAllocationModal,
    setDialerService,
    registrationCount,
    accountJoinList,
    authInfo,
    logoutUser,
    informators,
    servicePlanList,

    // permissions
    normalizedPerms,
    permsReady,
    permSig,
    hasPerm
}) => {

    const intl = useIntl();
    const rtl = useContext(RtlContext);
    const location = useLocation();
    const appType = useContext(AppTypeContext);
    const windowWidth = useWindowWidth();

    const accountsForMenu =
        (accountJoinList && accountJoinList.length)
            ? accountJoinList
            : (authInfo?.session?.account_list || []);

    const isTest = useMemo(() => {
        return authInfo?.session?.is_test;
    }, [authInfo?.session?.is_test]);

    const ratecardPerm = isTest
        ? [RATECARD_LIST_OTP_DEFAULT_DIALER, RATECARD_LIST_DEFAULT_DIALER, RATECARD_LIST_INCORPORATED_DEFAULT_DIALER]
        : [RATECARD_LIST_OTP_ACCOUNT_DIALER, RATECARD_LIST_ACCOUNT_DIALER, RATECARD_LIST_INCORPORATED_ACCOUNT_DIALER]


    const hideOnSmsSign = useMemo(() => {
        return accountsForMenu && accountsForMenu.length === 1
            && accountsForMenu.find(account => account.service === "sms")
    }, [accountsForMenu]);

    const accountList = useMemo(() => {
        return authInfo?.session ? authInfo?.session?.account_list : [];
    }, [authInfo?.session?.account_list, authInfo?.session?.session_key]);

    const hasReseller = useMemo(() => {
        return (accountList || []).some(a => a.service === "voice" && a.is_reseller);
    }, [accountList]);

    const servicePlanSig = useMemo(() => {
        return (servicePlanList || [])
            .map(p => `${p.gan ? 1 : 0}:${p.id || ""}`)
            .join("|");
    }, [servicePlanList, authInfo?.session?.session_key]);

    const accountsSig = useMemo(() => {
        return (accountsForMenu || [])
            .map(a => `${a.service}:${a.is_reseller ? 1 : 0}`)
            .sort()
            .join("|");
    }, [accountsForMenu, authInfo?.session?.session_key]);


    const menuDataAdmin = useMemo(() => {
        const filterMenuItems = (items, withService = false) => (
            items.reduce((sum, item) => {
                if (item.hide) return sum;

                if (item.checkedMethods && permsReady) {
                    for (const method of item.checkedMethods) {
                        const ok = hasPerm(method);
                        if (!ok) return sum;
                    }
                }

                const children = (item.children && item.children.length)
                    ? filterMenuItems(item.children, withService)
                    : null;

                const nextItem = {
                    ...item,
                    ...(item.key !== null && withService ? { key: `${item.key}` } : {}),
                    ...(children ? { children } : {})
                };

                if (nextItem.children && nextItem.children.length === 0) {
                    delete nextItem.children;
                    return sum;
                }

                return [...sum, nextItem];
            }, [])
        );

        const menuClientFilter = (menu) => {
            return menu.filter(item => !(isTest && item.hideOnTest && !item.hide))
                .reduce((data, value) => {
                    data.push({
                        ...value,
                        ...(value?.children ? { children: value.children.filter(item => !(isTest && item.hideOnTest) && !item.hide) } : {})
                    });

                    return data;
                }, []);
        };

        const menuAdminFilter = (menu) => {
            return menu.reduce((data, value) => {
                if (!value.hasOwnProperty("showOnService") || value?.showOnService === service) {
                    const nextItem = {
                        ...value,
                        ...(value?.children ? { children: value.children.filter(item => !item.hasOwnProperty("showOnService") || item?.showOnService === service) } : {})
                    };

                    if (nextItem.children && nextItem.children.length === 1 && !!nextItem.key) {
                        delete nextItem.children;
                    }

                    data.push(nextItem);
                }

                return data;
            }, []);
        };

        const sortMenuItems = (items = []) => {
            return [...items]
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                .map(item => (
                    item?.children?.length
                        ? { ...item, children: sortMenuItems(item.children) }
                        : item
                ));
        };

        const createServiceChildren = (accounts = [], pathname = "") => {
            if (typeof pathname === "string" || (Array.isArray(pathname) && pathname.length === 1)) {
                if (!accounts.length) {
                    return {}
                } else if (accounts.length === 1) {
                    const accountService = accounts[0];
                    const currentService = accountService?.service;

                    return {
                        onClick: (() => {
                            setDialerService(transformed_service_data[currentService]);
                        })
                    }
                } else {
                    return {
                        children: accounts.map(accountService => {
                            const currentService = accountService?.service;
                            const pathServiceKey = `${pathname}-${currentService}`;

                            let serviceName = currentService;

                            if (currentService === "voice") {
                                serviceName = intl.formatMessage(m.voice);
                            } else if (currentService === "sms") {
                                serviceName = intl.formatMessage(m.sms);
                            }

                            // const titleService = `${currentService[0].toUpperCase()}${currentService.slice(1)}`;

                            return {
                                key: pathServiceKey, title: serviceName, onClick: (() => {
                                    setDialerService(transformed_service_data[currentService]);
                                })
                            }
                        })
                    }
                }
            }

            if (Array.isArray(pathname) && pathname.length > 1) {
                const pathnameChildren = pathname.flatMap((path) => {
                    const { key, title, ...pathParams } = path;

                    if (!accounts.length) {
                        return [];
                    } else if (accounts.length === 1 || pathParams.noService) {
                        const accountService = accounts[0];
                        const currentService = accountService?.service;

                        const pathServiceKey = key;
                        const onClickFunc = !pathParams.noService ? {
                            onClick: (() => {
                                setDialerService(transformed_service_data[currentService]);
                            })
                        } : {};
                        return {
                            key: pathServiceKey, title: title, ...pathParams,
                            ...onClickFunc
                        }
                    }

                    return accounts.map(accountService => {
                        const currentService = accountService?.service;
                        const pathServiceKey = `${key}-${currentService}`;

                        let serviceName = currentService;

                        if (currentService === "voice") {
                            serviceName = intl.formatMessage(m.voice);
                        } else if (currentService === "sms") {
                            serviceName = intl.formatMessage(m.sms);
                        }

                        // const titleService = `${currentService[0].toUpperCase()}${currentService.slice(1)}`;

                        return {
                            key: pathServiceKey, title: `${title} ${serviceName}`, ...pathParams,
                            onClick: (() => {
                                setDialerService(transformed_service_data[currentService]);
                            })
                        }
                    });
                }).filter(item => item && item.key);

                return {
                    children: pathnameChildren
                };
            }
        };

        const roleList = authInfo?.session?.account_user?.role_list;
        const isRatemod = roleList && roleList.length ? roleList.includes("Ratemod") || roleList.includes("Ratemod (sms)") : null;

        if (isRatemod) {
            const checkMethods = servicePick(service, [INCORPORATED_GROUP_LIST_API], [SMS_SUPPLIER_GAN_GROUP_GET_LIST_METHOD])
            return menuAdminFilter([
                {
                    key: "/global-access-numbers",
                    title: "Global access numbers",
                    checkedMethods: checkMethods,
                    badge: service && informators.incorporated_group_without_payout_count &&
                        <>
                            <ServiceLink
                                to="/global-access-numbers?without-payout-only=1"
                                onClick={(e) => e.stopPropagation()}
                            />
                            {informators.incorporated_group_without_payout_count}
                        </>,
                    children: [
                        {
                            key: "/global-access-numbers",
                            title: "Global access numbers",
                            checkedMethods: checkMethods
                        },
                        { key: "/suppliers", title: "Suppliers", checkedMethods: [], showOnService: true },
                    ]
                },
            ]);
        }

        const clientMenu = [
            {
                order: 10,
                key: "/hot-accesses", title: intl.formatMessage(m.hotAccessList),
                ...createServiceChildren(accountsForMenu, "/hot-accesses")
            },
            { order: 20, key: "/live-calls", title: intl.formatMessage(m.liveCalls), icon: <Icon icon="phone-square" />, hide: !!hideOnSmsSign },
            ...(!isTest ? [{
                order: 30,
                key: null, title: intl.formatMessage(m.myNumbers),
                ...createServiceChildren(accountsForMenu,
                    [
                        {
                            key: "/numbers",
                            title: intl.formatMessage(m.myNumbers),
                            hideOnTest: true
                        },
                        {
                            key: "/price",
                            title: intl.formatMessage(m.ratesAndNumbers),
                            hideOnTest: false,
                            checkedMethods: ratecardPerm
                        }
                    ]
                )
            }] : [{
                order: 30,
                key: "/price",
                title: intl.formatMessage(m.ratesAndNumbers),
                ...createServiceChildren(accountsForMenu, "/price")
            }]),
            {
                order: 40,
                key: "/traffic", title: intl.formatMessage(m.report), hideOnTest: true,
                ...createServiceChildren(accountsForMenu, "/traffic")
            },
            {
                order: 50,
                key: null, title: intl.formatMessage(m.accesses),
                ...createServiceChildren(accountsForMenu,
                    [
                        {
                            key: "/accesses",
                            title: intl.formatMessage(m.accessList),
                        },
                        {
                            key: "/global-accesses-numbers",
                            title: intl.formatMessage(m.gaNumbers),
                            hide: servicePlanList && !servicePlanList.find(item => item.gan)
                        },
                        {
                            key: "/cli",
                            title: intl.formatMessage(m.cliLookup),
                            hideOnTest: true,
                        }
                    ]
                )
            },
            {
                order: 60,
                key: null, title: intl.formatMessage(m.other),
                ...createServiceChildren(accountsForMenu,
                    [
                        {
                            key: "/api",
                            title: "API",
                            hideOnTest: true,
                            noService: true
                        },
                        {
                            key: "/test-calls",
                            title: intl.formatMessage(m.testResults)
                        },
                        {
                            key: "/auth-numbers",
                            title: intl.formatMessage(m.otpNumbers),
                            hideOnTest: true,
                            noService: true,
                            hide: hideOnSmsSign
                        },
                        {
                            key: `/manual/${lang}`,
                            title: intl.formatMessage(m.manual),
                            noService: true
                        }
                        // {
                        //     key: "/allocations",
                        //     title: "Allocations",
                        //     hideOnTest: true
                        // }
                    ]
                )
            },
            { order: 70, key: "/payments", title: intl.formatMessage(m.payments), hideOnTest: true },
            {
                order: 80,
                key: null, title: intl.formatMessage(m.subaccounts), hideOnTest: true,
                hide: hideOnSmsSign || !hasReseller, checkedMethods: [SUBACC_GET_SUB_ACCOUNT_LIST_METHOD],
                children: [
                    { key: "/sub-accounts", title: intl.formatMessage(m.subaccounts) },
                    { key: "/sub-numbers", title: intl.formatMessage(m.numbers) },
                    { key: "/sub-traffic", title: intl.formatMessage(m.traffic) },
                    { key: "/sub-live-calls", title: intl.formatMessage(m.liveCalls) },
                    { key: "/sub-access-list", title: intl.formatMessage(m.accessList) },
                    { key: "/sub-settings", title: intl.formatMessage(m.settings) }
                ]
            }
        ];

        const adminMenu = [
            { order: 10, key: "/accounts", title: servicePick(service, "Accounts", "Dialers"), checkedMethods: [ACCOUNT_GET_LIST] },
            { order: 15, key: "/sms-suppliers", title: "Suppliers", showOnService: false },
            { order: 20, key: "/prices", title: "Prices" },
            {
                order: 30,
                key: null, title: "Traffic",
                children: [
                    // { key: "/full-report", title: "Full MDR Report", showOnService: false },
                    { key: "/cdr", title: servicePick(service, "CDR", "MDR") },
                    { key: "/traffic-reports", title: servicePick(service, "Traffic Report", "MDR Report") },
                    { key: "/failed-mdr", title: "Failed MDR", showOnService: false },
                    { key: "/failed-mdr-reports", title: "Failed MDR Report", showOnService: false },
                    { key: "/live-calls", title: "Live calls", showOnService: true },
                    { key: "/access-list", title: "Access list" },
                    { key: "/otp-rules", title: "OTP Rules", showOnService: false, checkedMethods: [OTP_RULE_API_GET_LIST] },
                    { key: "/otp-search", title: "OTP Search", showOnService: false, checkedMethods: [SMS_SEARCH_GET_LIST] },
                    { key: "/exceptions-accesses", title: "Exceptions for access lists", checkedMethods: [servicePick(service, ACCESS_EXCLUSION_LIST_API)], showOnService: true },
                    { key: "/manual-accesses", title: "Manual accesses", checkedMethods: [servicePick(service, MANUAL_ACCESS_LIST_GET_LIST_API, SMS_MANUAL_ACCESS_LIST_GET_LIST_API)] },
                ]
            },
            {
                order: 40,
                key: null, title: "Number allocation",
                children: [
                    {
                        key: buildSearch(location.search, { modal: "allocate_numbers" }),
                        title: "Allocate numbers",
                        onClick: openAllocationModal,
                        checkedMethods: [servicePick(service, ALLOCATION_TEMPLATE_API, SMS_ALLOCATION_TEMPLATE_API)]
                    },
                    { key: "/ranges-numbers", title: `Ranges & numbers` },
                    { key: "/blocked-destinations", title: "Blocked Destinations", checkedMethods: [servicePick(service, BLOCKED_DESTINATIONS_GET_LIST_API, SMS_BLOCKED_DESTINATIONS_GET_LIST_API)] },
                    // {key: "/sim-traffic-ranges", title: "SIM Traffic Ranges", checkedMethods: [SIM_TRAFFIC_RANGES_GET_LIST], showOnService: true},
                    { key: "/mass-revoke", title: "Mass revoke", checkedMethods: [servicePick(service, REVOKE_MASS_REVOKE_API, SMS_REVOKE_MASS_REVOKE_API)] },
                    { key: "/protected-ranges", title: "Protected ranges", checkedMethods: [servicePick(service, PROTECTED_RANGES_GET_LIST_API, SMS_PROTECTED_RANGES_GET_LIST_API)] }
                ]
            },
            { order: 50, key: "/payments", title: `Payment management`, checkedMethods: [PAYMENT_REQUEST_LIST_API] },
            {
                order: 60,
                key: null, title: "Tools", showOnService: true,
                children: [
                    { key: "/ivrs", title: "IVRs" },
                ]
            },
            {
                order: 70,
                key: null, title: "Settings",
                children: [
                    { key: "/auth-numbers", title: "Whitelisted CLIDs", showOnService: true },
                    { key: "/account-managers", title: "Account Managers", checkedMethods: [ACCOUNT_MANAGERS_REFERENCES_API] },
                    { key: "/admin-panel-users", title: "Admin Panel Users", checkedMethods: [MANAGER_ACCOUNT_USER_GET_LIST] },
                    { key: "/settings", title: "Admin panel settings", checkedMethods: ["global_setting:get_list"] },
                    { key: "/routing", title: "Routing", /*checkedMethods: ["global_setting:get_list"]*/ showOnService: false },
                    { key: "/e164", title: "E164", /*checkedMethods: ["global_setting:get_list"]*/ showOnService: true },
                    { key: "/e212", title: "E164/E212", /*checkedMethods: ["global_setting:get_list"]*/ showOnService: false },
                    { key: "/fake-dlr", title: "Fake DLR", showOnService: false },
                    // { key: "/exceptions-wrong-lang", title: "Exceptions wrong language traffic", showOnService: false }
                ]
            },
            {
                order: 80,
                key: null, title: "Logs",
                children: [
                    { key: "/action-logs", title: "Action Logs", checkedMethods: [ACTION_LOGS_GET_LIST_API] },
                    { key: "/am-activities", title: "AM activities", checkedMethods: [AM_ACTIVITIES_LIST_API] },

                ]
            },
            {
                order: 90,
                key: null, title: "News",
                children: [
                    { key: "/news-management", title: "News Manager", checkedMethods: [NEWS_LIST_API] },
                    { key: "/news-dialer-groups", title: "News Dialer Groups", checkedMethods: [DIALER_GROUPS_LIST_API] }
                ]
            },
            {
                order: 100,
                key: "/global-access-numbers",
                title: "Global access numbers",
                checkedMethods: [servicePick(service, INCORPORATED_GROUP_LIST_API, SMS_SUPPLIER_GAN_GROUP_GET_LIST_METHOD)],
                badge: service && informators.incorporated_group_without_payout_count &&
                    <>
                        <ServiceLink
                            to="/global-access-numbers?without-payout-only=1"
                            onClick={(e) => e.stopPropagation()}
                        />
                        {informators.incorporated_group_without_payout_count}
                    </>,
                children: [
                    {
                        key: "/global-access-numbers",
                        title: "Global access numbers",
                        checkedMethods: [servicePick(service, INCORPORATED_GROUP_LIST_API, SMS_SUPPLIER_GAN_GROUP_GET_LIST_METHOD)]
                    },
                    { key: "/suppliers", title: "Suppliers", checkedMethods: [], showOnService: true },
                ]
            },
        ];

        return appType === APP_TYPE_ADMIN
            ? sortMenuItems(filterMenuItems(menuAdminFilter(adminMenu), true))
            //: filterMenuItems(clientMenu.filter(item => !(isTest && item.hideOnTest)), false);
            : sortMenuItems(filterMenuItems(menuClientFilter(clientMenu), false));

    }, [
        normalizedPerms,
        appType,
        service,
        location.search,
        intl,
        authInfo?.session?.session_key,
        authInfo?.session?.account_user?.id,
        informators,
        isTest,
        hideOnSmsSign,
        permsReady,

        hasReseller,
        accountsForMenu,
        accountsSig,
        servicePlanList,
        servicePlanSig,
        openAllocationModal,
        setDialerService
    ]);
    const menuKey =
        `${authInfo?.session?.session_key || ""}|` +
        `${authInfo?.session?.account_user?.id || ""}|` +
        `${permSig}|${accountsSig}|${servicePlanSig}`;

    return (
        <>
            {windowWidth > 1200
                ? <MenuDesktop key={menuKey} registrationCount={registrationCount} data={menuDataAdmin} />
                : <MenuMobile key={menuKey} rtl={rtl} logoutUser={logoutUser} registrationCount={registrationCount} data={menuDataAdmin} m={m} />
            }
        </>
    );
};
